import Controller from '@ember/controller';
import { action } from '@ember/object';
// @ts-ignore
import Store from '@ember-data/store';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { cached, tracked } from '@glimmer/tracking';
import type { SegmentAfterSaveArgs } from 'donor-elf-web/components/journal/segments/edit-modal';
import type { onCopyTaskArgs } from 'donor-elf-web/components/task/info';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import {
  type AppliedPropertyFilter,
  type PropertyFilterInfo,
  deserializeAppliedFilters,
  serializeAppliedFilters,
} from 'donor-elf-web/lib/filter-utils-new';
import type { SelectOption } from 'donor-elf-web/lib/form-component-utils';
import {
  JournalRow,
  JournalStageColumn,
  type onJournalCellChangeArgs,
} from 'donor-elf-web/lib/journal-utils';
import logger from 'donor-elf-web/lib/logger';
import JournalContactInfo, {
  type DecisionStageNames,
} from 'donor-elf-web/lib/models/journal-contact-info';
import JournalInfo from 'donor-elf-web/lib/models/journal-info';
import JournalDashboardModel from 'donor-elf-web/models/journal-dashboard';
import JournalDecisionModel from 'donor-elf-web/models/journal-decision';
import RecommendationModel from 'donor-elf-web/models/recommendation';
import V2TaskModel from 'donor-elf-web/models/v2-task';
import AbilitiesService from 'donor-elf-web/services/abilities';
import AddSomethingModal, {
  type ContactAddedArgs,
} from 'donor-elf-web/services/add-something-modal';
import CampaignService from 'donor-elf-web/services/campaign-service';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import ContactsExportService from 'donor-elf-web/services/contacts-export';
import FormatDateService from 'donor-elf-web/services/format-date-service';
import FundsService from 'donor-elf-web/services/funds-service';
import JournalService from 'donor-elf-web/services/journal-service';
import TaskService from 'donor-elf-web/services/task-service';
import FileSaver from 'file-saver';
import Papa from 'papaparse';

const SORT_BY_MAPPING = {
  // 'dueByToday':   ['journalContactInfo.hasNotCompleteDueByTodayTasks:desc', 'journalContactInfo.earliestNotCompleteDueDate:asc', 'journalContactInfo.currentStageIndex:desc'],
  currentStage: [
    'journalContactInfo.journalContact.finished:asc',
    'journalContactInfo.lastStageIsCompleted:asc',
    'journalContactInfo.currentStageIndex:desc',
    'journalContactInfo.contact.name:asc',
  ],
  dueDate: [
    'journalContactInfo.hasNotCompleteTasks:desc',
    'journalContactInfo.earliestNotCompleteDueDate:asc',
    'journalContactInfo.currentStageIndex:desc',
    'journalContactInfo.contact.name:asc',
  ],
  contactName: ['journalContactInfo.contact.name:asc'],
};
const DEFAULT_SORT_BY_KEY = 'currentStage';

export const JOURNAL_COPY_AND_PASTE_ALL_CONFIRMATION =
  'Are you sure you want to paste this task to the same stage for all the other contacts the current filter is showing in the Journal?';

interface JournalPropertyFilterInfo extends PropertyFilterInfo {
  custom: {
    filter: (
      appliedFilter: AppliedPropertyFilter,
      filteredRows: JournalRow[],
    ) => Promise<JournalRow[]>;
  };
}

export default class JournalIndexController extends Controller {
  @service declare abilities: AbilitiesService;
  @service declare addSomethingModal: AddSomethingModal;
  @service declare campaignService: CampaignService;
  @service declare contactsCache: ContactsCache;
  @service declare contactsExport: ContactsExportService;
  @service declare formatDateService: FormatDateService;
  @service declare fundsService: FundsService;
  @service declare journalService: JournalService;
  @service declare powerMenu: any;
  @service declare store: Store;
  @service declare taskService: TaskService;

  declare model: {
    journalInfo: JournalInfo;
    journalDashboard: JournalDashboardModel;
  };

  queryParams = [
    'filters',
    {
      contactSearch: 'q',
    },
  ];

  @tracked filters?: string;
  @tracked filtering = false;
  @tracked appliedFilters?: AppliedPropertyFilter[];
  @tracked contactSearch?: string;
  @tracked newChangeAfterFilterApplied = false;

  @tracked percentLoaded: number = 0;
  @tracked loading = true;
  @tracked slimView = false;
  @tracked loadError?: any;
  @tracked hasReviewItems: boolean = false;
  @tracked allSegments: string[] = [];

  @tracked showAssignedUsersIfPresent: boolean = true;
  @tracked assignedUsersFilterOptions?: SelectOption[];
  @tracked taskToPaste?: V2TaskModel;
  @tracked copyAndPastingTask = false;
  @tracked copyAndPasteTaskError?: any;

  @tracked tableColumns: JournalStageColumn[] = [];

  journalContactInfos: JournalContactInfo[] = [];
  cardElement: any;
  templateElement!: HTMLElement;

  @tracked rows: JournalRow[] = [];
  sortedRows: JournalRow[] = [];
  @tracked filteredRows: JournalRow[] = [];
  @tracked sortByKey = DEFAULT_SORT_BY_KEY;

  @tracked editSegmentName?: string;
  @tracked showEditSegment = false;

  addSomethingContactAddedToken?: string;

  get campaign() {
    const campaignId = this.model.journalInfo.journal.campaignId;

    if (!campaignId || !this.campaignService.campaignsEnabled) {
      return null;
    }

    return this.campaignService.campaignsById[campaignId];
  }

  get sortBy() {
    // @ts-ignore;
    return SORT_BY_MAPPING[this.sortByKey];
  }

  sortByOptions = [
    {
      val: 'currentStage',
      label: 'Current Stage',
    },
    {
      val: 'dueDate',
      label: 'Due Date',
    },
    {
      val: 'contactName',
      label: 'Name',
    },
  ];

  sortTheRows() {
    this.sortedRows = sortByProperties(this.rows, this.sortBy);
  }

  async filterTheRows() {
    this.filtering = true;
    let filteredRows = this.sortedRows.slice();

    if ((this.contactSearch || '').length > 1) {
      let contactIds: any = {};
      for (let contact of this.contactsCache.search(this.contactSearch!)) {
        contactIds[contact.id] = true;
      }
      filteredRows = filteredRows.filter(
        (x) => contactIds[x.journalContactInfo.contactId!],
      );
    }

    for (let appliedFilter of this.appliedFilters || []) {
      let filterInfo = this.availablePropertyFilters.find(
        (x) => x.propertyName === appliedFilter.propertyName,
      )!;
      filteredRows = await filterInfo.custom.filter(
        appliedFilter,
        filteredRows,
      );
    }

    this.filteredRows = filteredRows;
    this.recalculateColumnCounts();
    this.newChangeAfterFilterApplied = false;
    this.filtering = false;
  }

  get showReapplyFilter() {
    return (
      this.newChangeAfterFilterApplied && (this.appliedFilters?.length || 0 > 0)
    );
  }

  @cached
  get availablePropertyFilters(): JournalPropertyFilterInfo[] {
    let list: JournalPropertyFilterInfo[] = [];
    list.push({
      propertyName: 'journalTasks',
      label: 'Task Status',
      propertyType: 'string',
      availableOperations: ['='],
      listOptions: ['All Complete', 'Some Not Complete', 'No Tasks'],
      custom: {
        filter: async (
          appliedFilter: AppliedPropertyFilter,
          filteredRows: JournalRow[],
        ) => {
          if (appliedFilter.val === 'All Complete') {
            return filteredRows.filter(
              (x) =>
                !x.journalContactInfo.hasNotCompleteTasks &&
                x.journalContactInfo.tasks.length > 0,
            );
          } else if (appliedFilter.val === 'Some Not Complete') {
            return filteredRows.filter(
              (x) => x.journalContactInfo.hasNotCompleteTasks,
            );
          } else if (appliedFilter.val === 'No Tasks') {
            return filteredRows.filter(
              (x) => x.journalContactInfo.tasks.length === 0,
            );
          } else {
            return filteredRows;
          }
        },
      },
    });
    list.push({
      propertyName: 'journalCurrentStage',
      label: 'Current Stage',
      propertyType: 'list',
      listOptions: [
        {
          val: -1,
          label: 'Not Started',
        },
        ...this.model.journalInfo.sortedStages.map((stage, index) => {
          return {
            val: index,
            label: stage.name,
          };
        }),
      ],
      availableOperations: ['any'],
      custom: {
        filter: async (
          appliedFilter: AppliedPropertyFilter,
          filteredRows: JournalRow[],
        ) => {
          let stageIndexVals = appliedFilter.val as unknown as number[];
          return filteredRows.filter((row) =>
            stageIndexVals.some(
              (stageIndex) =>
                row.journalContactInfo.currentStageIndex === stageIndex,
            ),
          );
        },
      },
    });
    if (this.allSegments.length > 0) {
      list.push({
        propertyName: 'journalSegment',
        label: 'Segment',
        propertyType: 'list',
        listOptions: this.allSegments,
        custom: {
          filter: async (
            appliedFilter: AppliedPropertyFilter,
            filteredRows: JournalRow[],
          ) => {
            let val = appliedFilter.val as string[];
            if (appliedFilter.operation === 'any') {
              return filteredRows.filter((row) =>
                val.some((x) =>
                  row.journalContactInfo.journalContact.segments.includes(x),
                ),
              );
            } else if (appliedFilter.operation === 'all') {
              return filteredRows.filter((row) =>
                val.every((x) =>
                  row.journalContactInfo.journalContact.segments.includes(x),
                ),
              );
            } else if (appliedFilter.operation === 'none') {
              return filteredRows.filter(
                (row) =>
                  !val.some((x) =>
                    row.journalContactInfo.journalContact.segments.includes(x),
                  ),
              );
            } else if (appliedFilter.operation === 'absent') {
              return filteredRows.filter(
                (row) =>
                  row.journalContactInfo.journalContact.segments.length === 0,
              );
            } else if (appliedFilter.operation === 'present') {
              return filteredRows.filter(
                (row) =>
                  row.journalContactInfo.journalContact.segments.length > 0,
              );
            } else {
              return filteredRows;
            }
          },
        },
      });
    }
    list.push({
      propertyName: 'journalDecisionsNoGift',
      label: 'Decisions Needing Attention',
      propertyType: 'no-operation',
      custom: {
        filter: async (
          _appliedFilter: AppliedPropertyFilter,
          filteredRows: JournalRow[],
        ) => {
          return filteredRows.filter((x) => {
            return x.journalContactInfo.journalDecisionInfos.some((y) => {
              return y.pledge && y.pledge.lastDonationDate == null;
              // Commenting out for now.  This was causing a lot of confusion since we look
              // at the last gift, not the gift associated with the pledge.  A follow up
              // story will address this.
              // return y.pledge && (
              //   y.pledge.lastDonationDate == null ||
              //   x.journalContactInfo.lastGift &&
              //   x.journalContactInfo.lastGift.amount < y.pledge.amount
              // );
            });
          });
        },
      },
    });
    list.push({
      propertyName: 'journalDecisionStage',
      label: 'Decision Stage',
      propertyType: 'list',
      availableOperations: ['any', 'none'],
      listOptions: [
        { val: 'notStarted', label: 'Not Started' },
        { val: 'preAsk', label: `${this.model.journalInfo.askStage.name}` },
        { val: 'waitingOnDecision', label: 'Waiting on Decision' },
        { val: 'decisionMade', label: 'Decision Made' },
      ],
      custom: {
        filter: async (
          appliedFilter: AppliedPropertyFilter,
          filteredRows: JournalRow[],
        ) => {
          let val = appliedFilter.val as DecisionStageNames[];
          if (appliedFilter.operation === 'any') {
            return filteredRows.filter((row) =>
              val.some((x) => row.journalContactInfo.decisionStage[x] === true),
            );
          } else if (appliedFilter.operation === 'none') {
            return filteredRows.filter(
              (row) =>
                !val.some(
                  (x) => row.journalContactInfo.decisionStage[x] === true,
                ),
            );
          } else {
            return filteredRows;
          }
        },
      },
    });
    list.push({
      propertyName: 'journalFinished',
      label: 'Finished',
      propertyType: 'boolean',
      availableOperations: ['='],
      custom: {
        filter: async (
          appliedFilter: AppliedPropertyFilter,
          filteredRows: JournalRow[],
        ) => {
          if ((appliedFilter.val as unknown as boolean) === true) {
            return filteredRows.filter(
              (x) => x.journalContactInfo.journalContact.finished,
            );
          } else {
            return filteredRows.filter(
              (x) => !x.journalContactInfo.journalContact.finished,
            );
          }
        },
      },
    });
    list.push({
      propertyName: 'journalRecommended',
      label: 'Was Recommended',
      propertyType: 'no-operation',
      custom: {
        filter: async (
          _appliedFilter: AppliedPropertyFilter,
          filteredRows: JournalRow[],
        ) => {
          return filteredRows.filter((x) => {
            return (
              (x.journalContactInfo.recommendedByContactIds || []).length > 0
            );
          });
        },
      },
    });

    let { assignedUsersOnTasksSelectOptions } = this;
    if (assignedUsersOnTasksSelectOptions.length > 0) {
      list.push({
        propertyName: 'journalAssignedUserId',
        label: 'Assigned User',
        propertyType: 'list',
        listOptions: assignedUsersOnTasksSelectOptions,
        custom: {
          filter: async (
            appliedFilter: AppliedPropertyFilter,
            filteredRows: JournalRow[],
          ) => {
            let userIds = appliedFilter.val as string[];
            if (appliedFilter.operation === 'any') {
              return filteredRows.filter((row) =>
                userIds.some((userIdVal) =>
                  row.journalContactInfo.tasks.some((task) =>
                    task.assignedUserIds?.some(
                      (taskUserId) => taskUserId === userIdVal,
                    ),
                  ),
                ),
              );
            } else if (appliedFilter.operation === 'all') {
              return filteredRows.filter((row) =>
                userIds.every((userIdVal) =>
                  row.journalContactInfo.tasks.some((task) =>
                    task.assignedUserIds?.some(
                      (taskUserId) => taskUserId === userIdVal,
                    ),
                  ),
                ),
              );
            } else if (appliedFilter.operation === 'none') {
              return filteredRows.filter(
                (row) =>
                  !userIds.some((userIdVal) =>
                    row.journalContactInfo.tasks.some((task) =>
                      task.assignedUserIds?.some(
                        (taskUserId) => taskUserId === userIdVal,
                      ),
                    ),
                  ),
              );
            } else if (appliedFilter.operation === 'present') {
              return filteredRows.filter((row) =>
                row.journalContactInfo.tasks.some(
                  (task) => (task.assignedUserIds || []).length > 0,
                ),
              );
            } else if (appliedFilter.operation === 'absent') {
              return filteredRows.filter((row) =>
                row.journalContactInfo.tasks.every(
                  (task) => (task.assignedUserIds || []).length === 0,
                ),
              );
            } else {
              return filteredRows;
            }
          },
        },
      });
    }

    let contactFilterInfos =
      this.contactsCache.availablePropertyFiltersForContacts.filter(
        (x) => x.custom.includeOnAnyReport,
      );
    for (let filterInfo of contactFilterInfos) {
      list.push({
        propertyName: filterInfo.propertyName,
        label: `Contact ${filterInfo.label}`,
        propertyType: filterInfo.propertyType,
        availableOperations: filterInfo.availableOperations,
        listOptions: filterInfo.listOptions,
        listIsSingleSelection: filterInfo.listIsSingleSelection,
        custom: {
          filter: async (
            appliedFilter: AppliedPropertyFilter,
            filteredRows: JournalRow[],
          ) => {
            let contactIds: any = {};
            for (let contact of await this.contactsCache.filter([
              appliedFilter,
            ])) {
              contactIds[contact.id] = true;
            }
            return filteredRows.filter(
              (x) => contactIds[x.journalContactInfo!.contactId!],
            );
          },
        },
      });
    }

    return list;
  }

  get assignedUsersOnTasksSelectOptions(): SelectOption[] {
    if (!this.abilities.hasAbility('assign_tasks')) {
      return [];
    }

    let assignedUserIds: any = {};
    for (let row of this.rows) {
      row.journalContactInfo.tasks.forEach((task) => {
        (task.assignedUserIds || []).forEach((userId) => {
          assignedUserIds[userId] = true;
        });
      });
    }
    return Object.keys(assignedUserIds).map((userId) => {
      return {
        val: userId,
        label: this.taskService.userLabelsById[userId],
      };
    });
  }

  @cached
  get contactNumbers() {
    let numbers = {
      total: 0,
      notStarted: 0,
      active: 0,
      finished: 0,
      filtered: this.filteredRows.length,
      changed: this.newChangeAfterFilterApplied, // only used to break the cache when there is a change
    };
    for (let row of this.rows) {
      numbers.total += 1;
      if (row.journalContactInfo.journalContact.finished) {
        numbers.finished += 1;
      } else if (
        row.journalContactInfo.tasks.length > 0 ||
        row.journalContactInfo.decisionMade
      ) {
        numbers.active += 1;
      } else {
        numbers.notStarted += 1;
      }
    }
    return numbers;
  }

  @action
  powerMenuOptions() {
    return [
      {
        label: 'Journal > Slim View',
        fn: () => {
          this.handleToggleSlimView();
        },
      },
      {
        label: 'Journal > Report',
        route: 'funds.fund.journals.journal.report',
      },
      {
        label: 'Journal > Edit',
        route: 'funds.fund.journals.journal.edit',
      },
      {
        label: 'Journal > Delete',
        route: 'funds.fund.journals.journal.delete',
      },
    ];
  }

  setTableColumns() {
    this.tableColumns = [];
    this.tableColumns.push(
      new JournalStageColumn({
        name: 'Contact',
        isContactColumn: true,
        isFixed: 'left',
        width: 200,
      }),
    );
    this.model.journalInfo.sortedStages.forEach((stage: any, index: number) => {
      this.tableColumns.push(
        new JournalStageColumn({
          name: stage.name,
          taskService: this.taskService,
          abilities: this.abilities,
          isStageColumn: true,
          isFollowUpColumn: stage.name === 'Follow Up',
          isDecisionColumn: stage.decisionStage,
          isLastStage: index + 1 === this.model.journalInfo.sortedStages.length,
          stageId: stage.id,
          width: this.slimView ? 110 : 165,
          stageIndex: index,
          lastTaskActionId: stage.lastTaskActionId,
          lastTaskMediumId: stage.lastTaskMediumId,
        }),
      );
    });
  }

  recalculateColumnCounts() {
    let columnsByStageId: any = {};
    let decisionsColumn = this.tableColumns.find((x) => x.isDecisionColumn);
    for (let column of this.tableColumns) {
      if (column.isStageColumn) {
        if (column.isDecisionColumn) {
          column.decisionsCount = 0;
        } else {
          column.unfinishedTasksCount = 0;
          column.finishedTasksCount = 0;
          columnsByStageId[column.stageId] = column;
        }
      }
    }
    for (let row of this.filteredRows) {
      if (decisionsColumn) {
        // only count one decision per contact, even if there are multiple decisions for the contact
        decisionsColumn.decisionsCount += Math.min(
          row.journalContactInfo.journalDecisions.length,
          1,
        );
      }
      for (let task of row.journalContactInfo.tasks) {
        if (columnsByStageId[task.journalStageId!]) {
          if (task.isComplete) {
            columnsByStageId[task.journalStageId!].finishedTasksCount += 1;
          } else {
            columnsByStageId[task.journalStageId!].unfinishedTasksCount += 1;
          }
        }
      }
    }
  }

  async loadReviewItems() {
    try {
      let journalId = this.model.journalInfo.journal.id;
      let contactDuplicates = await this.store.query('contact-duplicate', {
        filter: { journal_id: journalId, limit: 1 },
      });
      let reviewItems = await this.store.query('review-item', {
        filter: { journal_id: journalId, answered: false, limit: 1 },
      });
      this.hasReviewItems =
        (contactDuplicates.length as number) > 0 ||
        (reviewItems.length as number) > 0;
      this.load();
    } catch (error) {
      this.loadError = error;
    }
  }

  async load() {
    try {
      this.percentLoaded = 5;
      let journalId = this.model.journalInfo.journal.id;
      this.journalContactInfos =
        await this.journalService.loadJournalContactInfos(journalId, {
          forceReload: true,
          pageCallback: ({ downloadedCount, totalCount }: any) => {
            let percentLoaded = Math.floor(
              (downloadedCount * 100) / totalCount,
            );
            if (percentLoaded < 5) {
              percentLoaded = 5;
            }
            if (percentLoaded > 95) {
              percentLoaded = 95;
            }
            this.percentLoaded = percentLoaded;
          },
        });
      setTimeout(() => {
        this.percentLoaded = 95;
      });
      setTimeout(() => {
        this.rows = this.journalContactInfos.map((x) => new JournalRow(x));
        this.allSegments = this.journalService
          .getDistinctJournalSegments(this.journalContactInfos)
          .sort();
        this.setTableColumns();
        this.sortTheRows();
        this.filterTheRows();
        this.loading = false;
      }, 700);
    } catch (error) {
      this.loadError = error;
    }
  }

  toReviewLink?: HTMLElement;
  toReviewLinkTimeout?: NodeJS.Timeout;
  @action
  handleToReviewLinkInserted(el: HTMLElement) {
    this.toReviewLink = el;
    this.pulseToReviewLink();
  }

  @action
  handleToReviewLinkWillDestroy() {
    this.toReviewLink = undefined;
    if (this.toReviewLinkTimeout) {
      clearTimeout(this.toReviewLinkTimeout);
      this.toReviewLinkTimeout = undefined;
    }
  }

  pulseToReviewLink() {
    if (this.toReviewLink == null) {
      return;
    }
    this.toReviewLinkTimeout = setTimeout(() => {
      this.toReviewLinkTimeout = undefined;
      if (this.toReviewLink) {
        this.toReviewLink.classList.remove('bg-brand');
        this.toReviewLink.classList.remove('text-white');
        this.toReviewLink.classList.add('bg-white');
        this.toReviewLink.classList.add('text-brand');
        this.toReviewLinkTimeout = setTimeout(() => {
          this.toReviewLinkTimeout = undefined;
          if (this.toReviewLink) {
            this.toReviewLink.classList.add('bg-brand');
            this.toReviewLink.classList.add('text-white');
            this.toReviewLink.classList.remove('bg-white');
            this.toReviewLink.classList.remove('text-brand');
          }
          // pulse every 10 seconds
          this.toReviewLinkTimeout = setTimeout(() => {
            this.pulseToReviewLink();
          }, 10000);
        }, 1250);
      }
    }, 1500);
  }

  @action
  async handleCopyTask(args: onCopyTaskArgs) {
    if (args.pasteAll) {
      this.copyAndPastingTask = true;
      this.copyAndPasteTaskError = undefined;
      this.taskToPaste = undefined;
      let contactIds = this.filteredRows
        .filter((row) => {
          return row.journalContactInfo.contactId !== args.task.contactId;
        })
        .map((row) => row.journalContactInfo.contactId!);
      try {
        let pastedTasks = await this.taskService.copyAndPasteAll({
          task: args.task,
          contactIds: contactIds,
        });
        pastedTasks.forEach((pastedTask) => {
          if (pastedTask.id !== args.task.id) {
            let journalRow = this.rows.find(
              (x) => x.journalContactInfo.contactId === pastedTask.contactId,
            );
            if (journalRow) {
              journalRow.journalContactInfo.tasks.push(pastedTask);
              journalRow.journalContactInfo.recalculate();
            }
          }
        });
        this.copyAndPastingTask = false;
      } catch (error) {
        this.copyAndPasteTaskError = error;
        logger.error(error);
      }
    } else {
      this.taskToPaste = args.task;
    }
  }

  @action
  handleConfirmCopyAndPasteTaskError() {
    this.copyAndPastingTask = false;
    this.copyAndPasteTaskError = undefined;
  }

  @action
  handleCancelPasteTask() {
    this.taskToPaste = undefined;
  }

  @action
  handleToggleSlimView() {
    this.slimView = !this.slimView;
    this.setTableColumns();
    if (!this.slimView) {
      this.recalculateColumnCounts();
    }
  }

  @action
  handleFiltersChanged(currentFilters: AppliedPropertyFilter[]) {
    this.appliedFilters = currentFilters;
    this.contactSearch = undefined;
    this.serializeFilters();
    this.filterTheRows();
  }

  serializeFilters() {
    this.filters = serializeAppliedFilters(this.appliedFilters);
  }

  deserializeFilters() {
    this.appliedFilters = deserializeAppliedFilters(this.filters);
  }

  get filterLabel() {
    if (this.appliedFilters?.length || 0 > 0) {
      return `${this.appliedFilters!.length} Filter${this.appliedFilters!.length === 1 ? '' : 's'}`;
    } else {
      return 'Filter';
    }
  }

  @action
  handleSortByChanged(newVal: string) {
    this.sortByKey = newVal;
    this.sortTheRows();
    this.filterTheRows();
  }

  @action
  handleEditSegment(segment: string) {
    this.editSegmentName = segment;
    this.showEditSegment = true;
  }

  @action
  closeEditSegment() {
    this.editSegmentName = undefined;
    this.showEditSegment = false;
  }

  @action
  handleSegmentAfterSave(_args: SegmentAfterSaveArgs) {
    this.editSegmentName = undefined;
    this.showEditSegment = false;
    this.allSegments = this.journalService
      .getDistinctJournalSegments(this.journalContactInfos)
      .sort();
    this.filterTheRows();
  }

  @action
  handleToggleFilterSegment(segment: string) {
    let existingSegmentFilter = this.appliedFilters?.find(
      (x) => x.propertyName === 'journalSegment',
    );
    if (
      existingSegmentFilter &&
      (existingSegmentFilter.val as string[]).includes(segment)
    ) {
      this.handleFiltersChanged(
        this.appliedFilters!.filter((x) => x.propertyName !== 'journalSegment'),
      );
    } else {
      this.handleFiltersChanged([
        {
          propertyName: 'journalSegment',
          operation: 'any',
          val: [segment],
        },
      ]);
    }
  }

  @action
  handleJournalCellChange(args?: onJournalCellChangeArgs) {
    this.newChangeAfterFilterApplied = true;
    if (args) {
      if (args.taskSaved || args.decisionChanged) {
        this.recalculateColumnCounts();
      }
      if (args.taskSaved && args.taskPasted != true && this.taskToPaste) {
        // clear task to paste if the user saved another task
        this.taskToPaste = undefined;
      }
      if (args.contactSegmentsChanged) {
        this.allSegments = this.journalService
          .getDistinctJournalSegments(this.journalContactInfos)
          .sort();
      }
    }
  }

  @action
  handleRemoveJournalRow(journalRow: JournalRow) {
    this.journalService.removeJournalInfoForContactId(
      journalRow.journalContactInfo.contactId!,
    );
    this.rows = this.rows.filter((x) => x !== journalRow);
    this.sortTheRows();
    this.filterTheRows();
  }

  @action
  handleJournalRowActivate(args: { journalRow: JournalRow; active: boolean }) {
    if (args.journalRow.active !== args.active) {
      args.journalRow.active = args.active;
    }
    if (args.active) {
      this.rows.forEach((row) => {
        if (row.active && row !== args.journalRow) {
          row.active = false;
        }
      });
    }
  }

  @action
  handleContactSearchInput(e: any) {
    let val = e.target.value;
    if (isEmpty(val)) {
      val = undefined;
    }
    this.contactSearch = val;
    this.filterTheRows();
  }

  adjustCardHeight() {
    let windowHeight = window.innerHeight;
    let filterHeight = this.cardElement.querySelector(
      '[data-name="filter"]',
    ).offsetHeight;
    let newCardHeight =
      windowHeight - this.cardElement.offsetHeight + filterHeight;
    this.cardElement.style.height = `${newCardHeight}px`;
  }

  @action
  templateDidInsert(e: HTMLElement) {
    this.templateElement = e;
    this.rows = [];
    this.percentLoaded = 5;
    this.loading = true;
    this.deserializeFilters();

    this.powerMenu.prependOptionsFn('journal-index', this.powerMenuOptions);
    this.addSomethingContactAddedToken =
      this.addSomethingModal.subscribeToContactAdded(
        this.handleAddSomethingContactAdded,
      );

    this.loadReviewItems();
  }

  @action
  templateFilterDidInsert(el: HTMLElement) {
    this.cardElement = el.closest('.card-body');
    this.adjustCardHeight();
  }

  @action
  adjustSlimTableHeight(el: any) {
    let filterEl = el.children[0];
    let tableEl = el.children[1];
    let newTableHeight = window.innerHeight - filterEl.offsetHeight;
    tableEl.style.height = `${newTableHeight}px`;
  }

  @action
  templateWillDestroy() {
    this.loading = true;
    this.filters = undefined;
    this.filtering = false;
    this.appliedFilters = undefined;
    this.contactSearch = undefined;
    this.hasReviewItems = false;
    this.percentLoaded = 5;
    this.loadError = undefined;
    this.showEditSegment = false;
    if (this.addSomethingContactAddedToken) {
      this.addSomethingModal.unsubscribeWithToken(
        this.addSomethingContactAddedToken,
      );
    }
    this.powerMenu.removeOptions('journal-index');
  }

  @action
  handleGroupClicked(groupName: string) {
    this.handleFiltersChanged([
      {
        propertyName: 'contactGroups',
        operation: 'any',
        val: [groupName],
      },
    ]);
  }

  @action
  handleSegmentClicked(segment: string) {
    this.handleFiltersChanged([
      {
        propertyName: 'journalSegment',
        operation: 'any',
        val: [segment],
      },
    ]);
  }

  @action
  async handleRecommendedBySaved(recommendation: RecommendationModel) {
    this.refreshJournalContactInfosFor([
      recommendation.recommendedByContactId,
      recommendation.recommendedContactId,
    ]);
  }

  async refreshJournalContactInfosFor(contactIds: string[]) {
    let journalContactInfosToUpdate = this.rows
      .filter((x) =>
        contactIds.some((id) => id === x.journalContactInfo.contactId),
      )
      .map((x) => x.journalContactInfo);
    let journalContactIds = journalContactInfosToUpdate.map(
      (x) => x.journalContact.id,
    );

    let refreshedJournalContactDetails = await this.store.query(
      'all-funds/journal-contact-detail',
      { filter: { journal_contact_id: journalContactIds.join(',') } },
    );
    refreshedJournalContactDetails.forEach((detail) => {
      let info = journalContactInfosToUpdate.find(
        (x) => x.journalContact.id == detail.journalContact.id,
      );
      info?.updateFromJournalContactDetailModel(detail);
    });
  }

  @action
  async handleAddSomethingContactAdded(_msg: string, data: ContactAddedArgs) {
    if (data.addContactToJournal) {
      let journalContactInfo = await this.journalService.addContactToJournal(
        this.model.journalInfo.journal.id,
        data.contact,
      );
      // can use push because it's not tracked
      this.journalContactInfos.push(journalContactInfo);
      let row = new JournalRow(journalContactInfo);
      row.active = true;
      setTimeout(() => {
        row.active = false;
      }, 2000);
      this.rows = [...this.rows, row];
      this.sortedRows = [row, ...this.sortedRows];
      if (data.recommendedByContactId) {
        this.refreshJournalContactInfosFor([data.recommendedByContactId]);
      }
      // show the new contact on the top row
      this.filteredRows = [row, ...this.filteredRows];
      this.newChangeAfterFilterApplied = true;
      // have to first scroll to top then wait 100ms to give EmberTable time to
      // render the first row (which adjusts the scrollTop value) then scroll to
      // the top again because it's not fully in view
      this.templateElement.querySelector('.ember-table-overflow')!.scrollTop =
        0;
      setTimeout(() => {
        this.templateElement.querySelector('.ember-table-overflow')!.scrollTop =
          0;
      }, 100);
    }
  }

  @action
  async handleDownload() {
    let rows = [];

    let headerRow: [string | number] = ['Contact'];
    headerRow.push('');
    headerRow.push('Decision Made');
    headerRow.push('Decision Yes');
    headerRow.push('Decision Amount');
    headerRow.push('Pledge Amount');
    headerRow.push('Pledge Frequency');
    headerRow.push('');

    let stages = this.model.journalInfo.sortedStages;
    stages.forEach((stage) => {
      if (!stage.decisionStage) {
        headerRow.push(stage.name);
      }
    });
    headerRow.push('');

    let headerInfo = await this.contactsExport.addContactsHeaderColumns(
      headerRow,
      this.sortedRows.map((x) => x.journalContactInfo.contact!),
    );
    rows.push(headerRow);

    for (let journalRow of this.sortedRows) {
      let contact = journalRow.journalContactInfo.contact!;
      let { journalDecisions } = journalRow.journalContactInfo;

      let rowItems: { decision?: JournalDecisionModel }[] =
        journalDecisions.map((decision) => {
          return { decision };
        });
      if (rowItems.length === 0) {
        rowItems = [{ decision: undefined }];
      }

      let index = -1;
      for (let rowItem of rowItems) {
        index += 1;
        let row: (string | number | undefined)[] = [contact.name, ''];
        let { decision } = rowItem;
        if (decision) {
          let pledge = journalRow.journalContactInfo.pledges.find(
            (x) => x.id === decision!.pledgeId,
          );
          if (index === 0) {
            row.push('1');
            row.push(decision.yesNo ? '1' : '0');
          } else {
            row.push('');
            row.push('');
          }
          row.push(decision.amount);
          row.push(pledge?.amount);
          row.push(pledge?.frequencyLabel);
        } else {
          row.push('');
          row.push('');
          row.push('');
          row.push('');
          row.push('');
        }

        if (index === 0) {
          row.push('');
          stages.forEach((stage) => {
            if (!stage.decisionStage) {
              let taskCount = journalRow.taskInfosByStageId[stage.id].filter(
                (x) => x.task.isComplete,
              ).length;
              if (taskCount > 0) {
                row.push(taskCount.toString());
              } else {
                row.push('');
              }
            }
          });
          row.push('');
          await this.contactsExport.addContactInfoToRow(
            row,
            contact,
            headerInfo,
          );
        }
        rows.push(row);
      }
    }

    let csv = Papa.unparse(rows);
    let blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    FileSaver.saveAs(blob, 'journal.csv', true);
  }
}
